import { useEffect, useRef, useState } from "react";
import { Box, Spinner } from "@chakra-ui/react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import { FiChevronLeft, FiClock, FiLock } from "react-icons/fi";

import { VBabyColors } from "../../colors";
import TermsLabel from "../../components/TermsLabel";
import {
  BackButton,
  Body,
  Card,
  EmotionPanel,
  IconNote,
  PanelNote,
  PanelSubtitle,
  PanelTitle,
  PrimaryButton,
  Shell,
  TermsLine,
} from "../../components/vbaby";
import { useExam } from "../../contexts/examContext";
import { useSnackBar } from "../../contexts/snackbar";
import { boletoBuy, cardBuy } from "../../lib/api";
import { formatPrice } from "../../utils/formatPrice";
import { Form } from "./components/Form";
import { isPayLabelPrice, isSubmitDisabled } from "./rules";
export const MethodScreen = ({ method }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const [exam] = useExam();
  const { form, setForm } = useOutletContext();

  const [isCardComplete, setIsCardComplete] = useState(false);
  const [isSendingPayment, setIsSendingPayment] = useState(false);
  const [isSubmissionBlocked, setIsSubmissionBlocked] = useState(false);
  const isSubmittingRef = useRef(false);

  const price = exam?.payment?.price;

  const handleSubmitPurchase = async () => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSendingPayment(true);

    let purchase = null;

    try {
      if (method === "boleto") {
        const response = await boletoBuy({
          data: {
            address: {
              city: form.city,
              country: form.country,
              line1: form.line1,
              line2: form.line2,
              state: form.state,
              zip: form.zip,
            },
            email: form.email,
            name: form.name,
            cpf: form.cpf.replace(/\D/g, ""),
          },
        });

        purchase = { payment_method: "boleto", ...response.data };
      } else {
        const cardElement =
          stripe && elements ? elements.getElement(CardElement) : null;

        if (!cardElement) {
          showSnackBar(t("payment.errorToPay"), "error");
          setIsSubmissionBlocked(true);
          setIsSendingPayment(false);

          return;
        }

        const { token, error } = await stripe.createToken(cardElement);

        if (error) {
          showSnackBar(t("payment.errorToPay"), "error");
          setIsSubmissionBlocked(true);
          setIsSendingPayment(false);

          return;
        }

        const response = await cardBuy({
          data: {
            card_token: token.id,
            email: form.email,
            name: form.name,
            is_brazil_resident: form.isBrazilResident,
            ...(form.isBrazilResident
              ? { cpf: form.cpf.replace(/\D/g, "") }
              : {}),
          },
        });

        purchase = { payment_method: "card", ...response.data };
      }
    } catch {
      showSnackBar(t("payment.errorToPay"), "error");
      isSubmittingRef.current = false;
      setIsSendingPayment(false);

      return;
    }

    navigate(`/finish-purchase`, {
      state: { id: "", value: price, ...purchase },
    });
  };

  const isDisabled = isSubmitDisabled({ method, isCardComplete, form });

  const payLabel = isPayLabelPrice(price)
    ? t("payment.payAmount", { value: formatPrice(price) })
    : t("payment.endBuy");

  const subtitle = [
    t("payment.summaryTitle"),
    Number.isFinite(price) ? formatPrice(price) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Shell size="form">
      <BackButton type="button" onClick={() => navigate("/checkout")}>
        <FiChevronLeft aria-hidden="true" />
        {t("buttons.back")}
      </BackButton>

      <Card>
        <EmotionPanel align="start" density="header">
          <PanelTitle size="header">
            {method === "card" ? t("payment.creditCard") : t("payment.boleto")}
          </PanelTitle>
          <PanelSubtitle fontSize="12.5px">{subtitle}</PanelSubtitle>
          {method === "boleto" && (
            <PanelNote alignItems="flex-start" marginTop="6px">
              <FiClock aria-hidden="true" />
              {t("payment.boletoDelay")}
            </PanelNote>
          )}
        </EmotionPanel>

        <Box>
          <Form
            id="contact"
            variant="contact"
            paymentMethod={method}
            form={form}
            setForm={setForm}
          />
          {method === "boleto" && (
            <Form
              id="address"
              variant="address"
              form={form}
              setForm={setForm}
            />
          )}
          {method === "card" && (
            <Form
              id="card"
              variant="card"
              form={form}
              setForm={setForm}
              handleCardChange={(event) => setIsCardComplete(event.complete)}
            />
          )}
        </Box>

        <Body>
          <PrimaryButton
            type="button"
            disabled={isDisabled || isSendingPayment || isSubmissionBlocked}
            aria-busy={isSendingPayment}
            aria-label={isSendingPayment ? payLabel : undefined}
            onClick={handleSubmitPurchase}
          >
            {isSendingPayment ? <Spinner size="sm" borderWidth="2px" /> : payLabel}
          </PrimaryButton>

          <IconNote justifyContent="center">
            <FiLock aria-hidden="true" />
            {t("payment.dataProtected")}
          </IconNote>

          <TermsLine>
            <TermsLabel
              label={t("payment.termsAgreement")}
              linkColor={VBabyColors.primaryDark}
            />
          </TermsLine>
        </Body>
      </Card>
    </Shell>
  );
};
